import type {
  SocialSalesSourceCreateInput,
  SocialSalesSourceUpdateInput,
} from "@/modules/social-sales-sources/domain/contracts";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { socialSalesSourcesApi } from "./client";

export const socialSalesSourcesKeys = {
  all: ["social-sales-sources"] as const,
  list: (activeOnly: boolean) =>
    [...socialSalesSourcesKeys.all, "list", activeOnly] as const,
  detail: (id: string) =>
    [...socialSalesSourcesKeys.all, "detail", id] as const,
};

export function useSocialSalesSourcesListQuery(activeOnly = false) {
  return useQuery({
    queryKey: socialSalesSourcesKeys.list(activeOnly),
    queryFn: () => socialSalesSourcesApi.list(activeOnly),
  });
}

/** ສຳລັບ POS — ອະນຸຍາດໃຫ້ໂຫຼດເມື່ອມີສິດ POS ຫຼືອ່ານອໍເດີ້ (ເບິ່ງ backend middleware list ກົງກັນ) */
export function useSocialSalesSourcesActiveForPosQuery(enabled: boolean) {
  return useQuery({
    queryKey: socialSalesSourcesKeys.list(true),
    queryFn: () => socialSalesSourcesApi.list(true),
    enabled,
    staleTime: 60_000,
  });
}

export function useSocialSalesSourceQuery(id: string) {
  return useQuery({
    queryKey: socialSalesSourcesKeys.detail(id),
    queryFn: () => socialSalesSourcesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateSocialSalesSourceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SocialSalesSourceCreateInput) =>
      socialSalesSourcesApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: socialSalesSourcesKeys.all });
      toast.success("ສ້າງແຫຼ່ງຂາຍສຳເລັດ");
    },
    onError: (e: unknown) => {
      toast.error("ສ້າງບໍ່ສຳເລັດ", {
        description: e instanceof Error ? e.message : undefined,
      });
    },
  });
}

export function useUpdateSocialSalesSourceMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SocialSalesSourceUpdateInput) =>
      socialSalesSourcesApi.patch(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: socialSalesSourcesKeys.detail(id) });
      qc.invalidateQueries({ queryKey: socialSalesSourcesKeys.all });
      toast.success("ບັນທຶກສຳເລັດ");
    },
    onError: (e: unknown) => {
      toast.error("ບັນທຶກບໍ່ສຳເລັດ", {
        description: e instanceof Error ? e.message : undefined,
      });
    },
  });
}
